// Module: useFitScale
// -----------------------------------------------------------------
// Receives:  a ref to a box, and the fixed size of the stage drawn in it
// Produces:  the scale that fits the stage to the box, kept current on resize
// Owns:      the ResizeObserver behind each measurement
// Does NOT:  size the box or apply the transform (→ callers)
// -----------------------------------------------------------------
import { RefObject, useLayoutEffect, useState } from "react";

/**
 * Report `read(el)` now and on every resize while `isEnabled`; zero (a box
 * not laid out yet) is skipped.
 *
 * Input:  deps — extra values that remount the observer (e.g. a swapped box)
 */
export const useMeasured = (
  ref: RefObject<HTMLElement | null>,
  isEnabled: boolean,
  read: (el: HTMLElement) => number,
  onChange: (value: number) => void,
  deps: unknown[] = [],
) => {
  useLayoutEffect(() => {
    if (!isEnabled) return;
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const value = read(el);
      if (value > 0) onChange(value);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, ...deps]);
};

/**
 * Scale for a fixed-size stage inside `ref`'s box: fit to its width, or, with
 * a `height`, fit wholly inside it.
 *
 * Input:  initialScale — returned until the box is first measured
 * Output: the current scale
 */
export const useFitScale = (
  ref: RefObject<HTMLElement | null>,
  stage: { width: number; height?: number },
  isEnabled: boolean,
  initialScale: number,
  deps: unknown[] = [],
): number => {
  const [scale, setScale] = useState(initialScale);
  const { width, height } = stage;
  useMeasured(
    ref,
    isEnabled,
    (el) => {
      if (height === undefined) return el.clientWidth / width;
      const { clientWidth, clientHeight } = el;
      if (clientWidth <= 0 || clientHeight <= 0) return 0;
      return Math.min(clientWidth / width, clientHeight / height);
    },
    setScale,
    [width, height, ...deps],
  );
  return scale;
};
